import { Icon } from '@/components/Icon';
import { CustomSelect } from '@/components/form/CustomSelect';
import { CustomDatePicker } from '@/components/form/CustomDatePicker';
import { useAsyncData } from '@/hooks/useAsyncData';
import { fetchSegmentTests, type BannerSegment, type BannerSegmentType } from '@/api/banners';
import { AUDIENCE_LABEL } from './audience';

export function BannerAudience({
  segments,
  onChange,
  disabled,
}: {
  segments: BannerSegment[];
  onChange: (segments: BannerSegment[]) => void;
  disabled: boolean;
}) {
  // Список тестов не критичен для остальной формы: без него правило
  // «работающие с тестом» просто не даст выбрать тест, остальное работает.
  const { data } = useAsyncData(fetchSegmentTests, []);
  const tests = data ?? [];

  const add = (type: BannerSegmentType) => {
    onChange([...segments, { type, data: null, dateFrom: null, dateTo: null }]);
  };

  const patch = (index: number, patchValue: Partial<BannerSegment>) => {
    onChange(segments.map((s, i) => (i === index ? { ...s, ...patchValue } : s)));
  };

  const removeAt = (index: number) => onChange(segments.filter((_, i) => i !== index));

  const alreadyHasAll = segments.some((s) => s.type === 'all');

  return (
    <div className="audience">
      {segments.length === 0 ? (
        <p className="audience__empty">
          Правила не заданы — баннер никто не увидит. Добавьте хотя бы одно правило показа.
        </p>
      ) : (
        <ul className="audience__list">
          {segments.map((segment, index) => (
            <li key={index} className="audience__item">
              <div className="audience__head">
                <span className="badge badge--info">{AUDIENCE_LABEL[segment.type]}</span>
                <button
                  type="button"
                  className="audience__remove"
                  aria-label={`Убрать правило «${AUDIENCE_LABEL[segment.type]}»`}
                  disabled={disabled}
                  onClick={() => removeAt(index)}
                >
                  <Icon name="close" size={14} />
                </button>
              </div>

              {segment.type === 'test' && (
                <label className="edit-field">
                  <span>Тест *</span>
                  <CustomSelect
                    value={segment.data ?? ''}
                    disabled={disabled || tests.length === 0}
                    options={tests.map((t) => ({ value: t.value, label: t.label }))}
                    onChange={(value) => patch(index, { data: value })}
                  />
                  <span className="edit-field__hint">
                    Баннер увидят пользователи при работе с выбранным тестом.
                  </span>
                </label>
              )}

              {segment.type === 'active' && (
                <div className="audience__dates">
                  <label className="edit-field">
                    <span>Активность с</span>
                    <CustomDatePicker
                      value={segment.dateFrom ?? ''}
                      onChange={(value) => patch(index, { dateFrom: value || null })}
                    />
                  </label>
                  <label className="edit-field">
                    <span>Активность до</span>
                    <CustomDatePicker
                      value={segment.dateTo ?? ''}
                      onChange={(value) => patch(index, { dateTo: value || null })}
                    />
                  </label>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="audience__add">
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          disabled={disabled || alreadyHasAll}
          title={alreadyHasAll ? 'Правило «Все пользователи» уже добавлено' : undefined}
          onClick={() => add('all')}
        >
          <Icon name="users" size={14} /> Все пользователи
        </button>
        <button type="button" className="btn btn--secondary btn--sm" disabled={disabled} onClick={() => add('test')}>
          <Icon name="testChecklist" size={14} /> Работающие с тестом
        </button>
        <button type="button" className="btn btn--secondary btn--sm" disabled={disabled} onClick={() => add('active')}>
          <Icon name="clock" size={14} /> Активные в период
        </button>
      </div>
    </div>
  );
}
